import React from 'react'
import { Box, Button, Flex, Message, Modal, Text, toast } from '@workwhile/ui'
import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import {
  authorizeAdminFormSchema,
  AuthorizeAdminFormSchema,
} from '../../../schema'
import { UserPicker } from '../../../components/UserPicker'
import { useUserPermissionsWorkflowsContextValue } from '../../../context/UserPermissionsWorkflowsProvider'
import { useAuthorizeAdminFlowContextValue } from '../context/AuthorizeAdminFlowProvider'

type SelectedUser = NonNullable<
  React.ComponentProps<typeof UserPicker>['selectedUser']
>

export const AuthorizeAdminFlowMain = () => {
  const {
    authorizeAdminFlow: { isOpen, close },
  } = useUserPermissionsWorkflowsContextValue()
  const {
    state: {
      mutationState: { isPending, errorMessage },
    },
    actions: { submit, reset: resetMutation },
  } = useAuthorizeAdminFlowContextValue()
  const {
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm<AuthorizeAdminFormSchema>({
    resolver: zodResolver(authorizeAdminFormSchema),
    defaultValues: {
      userId: undefined,
      name: '',
      email: '',
    },
  })

  const userId = watch('userId')
  const name = watch('name')
  const email = watch('email')
  const selectedUser: SelectedUser | null =
    userId && name ? { userId, name, email } : null

  const handleClose = () => {
    reset()
    resetMutation()
    close()
  }

  const onSubmit = handleSubmit(({ userId: selectedUserId }) => {
    submit(
      { userId: selectedUserId },
      {
        onSuccess: ({ existing }) => {
          toast.success(
            existing
              ? 'User is already a WorkWhile admin'
              : 'WorkWhile admin set up successfully',
            { position: 'top-center' }
          )
          handleClose()
        },
      }
    )
  })

  const handleUserChange = (user: SelectedUser | undefined) => {
    if (!user) {
      reset()
      return
    }

    setValue('userId', user.userId)
    setValue('name', user.name)
    setValue('email', user.email)
  }

  return (
    <Modal
      open={isOpen}
      onClose={handleClose}
      title="Set up WorkWhile admin"
      position="top"
      loading={isPending}
      showCancel
      customCta={
        <Flex>
          <Button variant="text" onClick={handleClose}>
            Cancel
          </Button>
          <Button variant="primary" onClick={onSubmit}>
            Confirm
          </Button>
        </Flex>
      }
    >
      <Box
        as="form"
        onSubmit={(event) => {
          event.preventDefault()
          onSubmit()
        }}
      >
        <Message variant="info" mt={3} borderRadius="small">
          This grants WorkWhile admin and internal company access. Only users
          with a <code>@workwhilejobs.com</code> email address are eligible.
        </Message>

        <Box mt={4}>
          <UserPicker
            value={userId}
            selectedUser={selectedUser}
            error={errors.userId?.message}
            onChange={handleUserChange}
          />
        </Box>

        {selectedUser ? (
          <Text mt={3}>
            Set up WorkWhile admin for <strong>{selectedUser.name}</strong> (
            {selectedUser.email})?
          </Text>
        ) : null}

        {errorMessage ? (
          <Message variant="error" mt={3} borderRadius="small">
            {errorMessage}
          </Message>
        ) : null}
      </Box>
    </Modal>
  )
}
